"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import DataTable, { FilterOption } from "../../components/commons/DataTable";
import Button from "../../components/commons/Button";
import type { Column } from "../../components/commons/DataTable";
import { getCommunityQuestions } from "../../services/webServices/community/questions/get";
import { patchCommunityQuestion } from "../../services/webServices/community/questions/patch";
import type { CommunityQuestion } from "../../services/webServices/community/questions/types";
import { Eye, ArrowRight2, ArrowLeft2 } from "iconsax-reactjs";
import { getGradeLabel, getBookLabel } from "../../constants/education";

const STATUS_MAP = {
  0: { label: "منتشر نشده", color: "bg-gray-100 text-gray-700" },
  1: { label: "منتشر شده", color: "bg-green-100 text-green-700" },
  2: { label: "رد شده", color: "bg-red-100 text-red-700" },
};

const ROLE_MAP = {
  5: "معلم",
  2: "دانش‌آموز",
};

const FILE_URL = process.env.REACT_APP_File_URL || "";
const filterOptions: FilterOption[] = [
  { value: "0", label: "تعیین وضعیت نشده" },
  { value: "1", label: "تایید شده" },
  { value: "2", label: "رد شده" },
];

export default function SepehrJoQuestions() {
  const [questions, setQuestions] = useState<CommunityQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedQuestion, setSelectedQuestion] =
    useState<CommunityQuestion | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );
  const [filterValue, setFilterValue] = useState<string>("");
  const pageSize = 10;

  // Fetch data
  const fetchQuestions = useCallback(
    async (page: number, search?: string, statusValue?: string) => {
      setLoading(true);
      try {
        const effectiveFilterValue =
          statusValue !== undefined ? statusValue : filterValue;
        const parsedStatus = effectiveFilterValue
          ? Number(effectiveFilterValue)
          : null;

        const response = await getCommunityQuestions({
          params:
            page === 1 && !search && parsedStatus === null
              ? undefined
              : {
                  ...(page > 1 && { page }),
                  ...(search && { q: search }),
                  ...(parsedStatus !== null && { publish_status: parsedStatus }),
                },
        });

        setQuestions(response.results || []);
        setTotalItems(response.count || 0);
      } catch (error) {
        console.error("Error fetching questions:", error);
        setQuestions([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [filterValue],
  );

  useEffect(() => {
    fetchQuestions(1);
  }, [fetchQuestions]);

  // Handle search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setCurrentPage(1);
      fetchQuestions(1, value);
    },
    [fetchQuestions],
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchQuestions(page, searchValue);
    },
    [searchValue, fetchQuestions],
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (value: string) => {
      setFilterValue(value);
      setCurrentPage(1);
      setSearchValue("");
      fetchQuestions(1, "", value);
    },
    [fetchQuestions],
  );

  // Handle open modal
  const handleOpenModal = useCallback((question: CommunityQuestion) => {
    setSelectedQuestion(question);
    setIsModalOpen(true);
  }, []);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedQuestion(null), 300);
  }, []);

  // Handle image preview
  const handleImageClick = useCallback((imageSrc: string, index: number) => {
    setSelectedImageSrc(imageSrc);
    setSelectedImageIndex(index);
  }, []);

  const closeImagePreview = useCallback(() => {
    setSelectedImageSrc(null);
    setSelectedImageIndex(null);
  }, []);

  const imagePreviewItems = useMemo(
    () => selectedQuestion?.attachments || [],
    [selectedQuestion?.attachments],
  );

  // Handle patch question
  const handlePatchQuestion = useCallback(
    async (id: number, publishStatus: number) => {
      setModalLoading(true);
      try {
        const result = await patchCommunityQuestion({
          id,
          body: { publish_status: publishStatus },
        });

        if (result) {
          setSelectedQuestion(result);
          // Refresh questions list and close modal
          fetchQuestions(currentPage, searchValue);
          setTimeout(() => handleCloseModal(), 300);
        }
      } catch (error) {
        console.error("Error updating question:", error);
      } finally {
        setModalLoading(false);
      }
    },
    [currentPage, searchValue, fetchQuestions, handleCloseModal],
  );

  // Define columns
  const columns: Column<CommunityQuestion>[] = useMemo(
    () => [
      {
        key: "id",
        header: "شماره سؤال",
        accessor: (row) => (
          <a
            href={`https://sepehrjou.ir/questions/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primaryVar-500 underline-offset-4 hover:underline"
          >
            {row.id}
          </a>
        ),
        sortable: true,
        width: "100px",
        align: "center",
      },
      {
        key: "user.full_name",
        header: "نام کاربر",
        accessor: (row) => row.user?.full_name || "-",
        width: "150px",
      },
      {
        key: "user.role",
        header: "نوع کاربر",
        accessor: (row) => {
          const roleKey = row.user?.role as keyof typeof ROLE_MAP;
          return ROLE_MAP[roleKey] || "-";
        },
        width: "120px",
      },
      {
        key: "created_at",
        header: "زمان انتشار",
        accessor: (row) => {
          const date = new Date(row.created_at);
          return date.toLocaleDateString("fa-IR");
        },
        sortable: true,
        width: "130px",
      },
      {
        key: "publish_status",
        header: "وضعیت انتشار",
        accessor: (row) => {
          const status =
            STATUS_MAP[row.publish_status as keyof typeof STATUS_MAP];
          return (
            <span
              className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                status?.color || "bg-gray-100 text-gray-700"
              }`}
            >
              {status?.label || "نامشخص"}
            </span>
          );
        },
        width: "130px",
        align: "center",
      },
      {
        key: "details",
        header: "جزییات",
        accessor: (row) => (
          <button
            onClick={() => handleOpenModal(row)}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-blue-600 transition-colors hover:bg-blue-100"
            title="مشاهده جزییات"
          >
            <Eye size="18" />
          </button>
        ),
        width: "100px",
        align: "right",
      },
    ],
    [handleOpenModal],
  );

  return (
    <div className="w-full" dir="rtl">
      <DataTable<CommunityQuestion>
        filterable
        filterPlaceholder="همه سوالات"
        filterOptions={filterOptions}
        filterValue={filterValue}
        onFilterChange={handleFilterChange}
        data={questions}
        columns={columns}
        title="مدیریت سؤالات سپهرجو"
        searchable
        searchPlaceholder="جستجو بر اساس نام کاربر یا محتوا..."
        searchKeys={["content"] as (keyof CommunityQuestion)[]}
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        loading={loading}
        loadingText="در حال بارگزاری سؤالات..."
        emptyText="سؤالی یافت نشد"
        pagination
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        serverSidePagination
      />

      {/* Modal */}
      {isModalOpen && selectedQuestion && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={handleCloseModal}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                سؤال #{selectedQuestion.id}
              </h2>
              <button
                onClick={handleCloseModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-4">
              {/* User Info */}
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-1 lg:gap-3">
                  <div className="flex items-start gap-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 shrink-0">
                      <img
                        src={`${FILE_URL}${selectedQuestion.user?.avatar || ""}`}
                        alt={selectedQuestion.user?.full_name || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <p className="text-sm font-bold text-gray-800">
                        {selectedQuestion.user?.full_name || "-"}
                      </p>
                      <div className="hidden lg:flex items-center gap-2">
                        <span className="inline-block rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-medium">
                          پایه {getGradeLabel(selectedQuestion.education_grade || "-")}
                        </span>
                        <span className="inline-block rounded-full bg-purple-100 text-purple-700 px-3 py-1 text-xs font-medium">
                          {getBookLabel(selectedQuestion.book || "-")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-end gap-3 text-sm sm:flex-row sm:items-center sm:gap-3">
                    <span className="text-gray-500 text-xs whitespace-nowrap inline-block">
                      {new Date(selectedQuestion.created_at).toLocaleDateString(
                        "fa-IR",
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 lg:hidden">
                  <span className="inline-block rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-medium">
                    پایه {getGradeLabel(selectedQuestion.education_grade || "-")}
                  </span>
                  <span className="inline-block rounded-full bg-purple-100 text-purple-700 px-3 py-1 text-xs font-medium">
                    {getBookLabel(selectedQuestion.book || "-")}
                  </span>
                </div>
              </div>

              {/* Question Content */}
              <p className="text-xs text-gray-900 font-normal text-right">
                {selectedQuestion.content}
              </p>

              {/* Attachments */}
              {selectedQuestion.attachments &&
                selectedQuestion.attachments.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedQuestion.attachments.map((attachment, index) => (
                      <button
                        key={attachment.id || index}
                        type="button"
                        onClick={() =>
                          handleImageClick(attachment.file_content, index)
                        }
                        className="w-[88px] h-[88px] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity"
                        aria-label="نمایش تصویر در حالت تمام صفحه"
                      >
                        <img
                          src={`${FILE_URL}${attachment.file_content}`}
                          alt={`پیوست ${index + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                    ))}
                  </div>
                )}

              {/* Action Buttons */}
              <div className="flex items-end justify-end gap-3 pt-4 mt-4">
                <Button
                  variant="primary"
                  style="solid"
                  size="md"
                  text="تایید سوال"
                  loading={modalLoading}
                  disabled={modalLoading}
                  onClick={() => handlePatchQuestion(selectedQuestion.id, 1)}
                />
                <Button
                  variant="primary"
                  style="solid"
                  size="md"
                  text="رد سوال"
                  loading={modalLoading}
                  disabled={modalLoading}
                  onClick={() => handlePatchQuestion(selectedQuestion.id, 2)}
                  customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {selectedImageSrc && (
        <div
          className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <div
            className="relative flex h-[min(82vh,720px)] w-full max-w-5xl items-center justify-center rounded-2xl px-12 py-14 sm:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            {imagePreviewItems.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setSelectedImageIndex((prev) =>
                    prev === null
                      ? 0
                      : (prev - 1 + imagePreviewItems.length) %
                        imagePreviewItems.length,
                  )
                }
                className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/45 text-white sm:right-5"
              >
                <ArrowRight2 size={20} />
              </button>
            )}
            {imagePreviewItems.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  setSelectedImageIndex((prev) =>
                    prev === null ? 0 : (prev + 1) % imagePreviewItems.length,
                  )
                }
                className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/45 text-white sm:left-5"
              >
                <ArrowLeft2 size={20} />
              </button>
            )}
            <button
              type="button"
              onClick={closeImagePreview}
              className="absolute top-4 right-4 z-10 text-white text-sm border border-white/40 rounded-md px-3 py-1 bg-black/35"
            >
              بستن
            </button>
            <img
              src={`${FILE_URL}${selectedImageSrc}`}
              alt="question-attachment-fullscreen"
              className="h-full max-h-full w-full max-w-full object-contain rounded-xl"
            />
            {imagePreviewItems.length > 1 && selectedImageIndex !== null && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
                {selectedImageIndex + 1} / {imagePreviewItems.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
